import { AppBar, Box, Button, Stack, Toolbar, Tooltip } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { LogoutButton } from "components/LogoutButton";
import {
  isRouteActive,
  NAV_ROUTES,
  NavBarButtonWrapper,
} from "components/NavigationBar";
import { CarrierSearchBar } from "components/Shipper/components/CarrierSearchBar";
import { UserAccountButton } from "components/UserAccountButton";
import { BG_ICON_COLOR } from "constants/index";
import { useCompanyHasPaymentMethod, useRoleContext } from "hooks";
import { useCompanyProfile } from "hooks/useCompanyProfile";
import { useSubscriptionStatus } from "hooks/useSubscriptionStatus";
import { isFreeCarrierSubscription } from "utils";

import { CompanyProfileBanner } from "./components/CompanyProfileBanner";
import { FillCarrierProfileBanner } from "./components/FillCarrierProfileBanner";
import { FreeCarrierBanner } from "./components/FreeCarrierBanner";
import { FreeTierShipperBanner } from "./components/FreeTierShipperBanner";
import { PaidCarrierBanner } from "./components/PaidCarrierBanner";
import { PpbPaymentMethodBanner } from "./components/PpbPaymentMethodBanner";
import { TrialShipperBanner } from "./components/TrialShipperBanner";
import { UnverifiedCarrierBanner } from "./components/UnverifiedCarrierBanner";
import { UnverifiedShipperBanner } from "./components/UnverifiedShipperBanner";
import { BANNER_CTA_BUTTON_SX } from "./constants";

import type { CompanyProfile } from "types/company";

interface Props {
  isMobile: boolean;
  openNavBarIcon: React.ReactNode;
}

interface BannerContentParams {
  company: ReturnType<typeof useRoleContext>["company"];
  subscription: ReturnType<typeof useSubscriptionStatus>["subscription"];
  isLoading: boolean;
  carrierProfile: CompanyProfile | null;
  isCarrierProfileLoading: boolean;
  isPaymentMethodLookupPending: boolean;
  isPayPerBookingCarrierWithoutPaymentMethod: boolean;
}

function getBannerContent(params: BannerContentParams): React.ReactNode {
  const {
    company,
    subscription,
    isLoading,
    carrierProfile,
    isCarrierProfileLoading,
    isPaymentMethodLookupPending,
    isPayPerBookingCarrierWithoutPaymentMethod,
  } = params;

  if (isLoading) {
    return null;
  }
  // External carriers were invited by a shipper and quote partner shipments
  // without friction — they get no verification or upgrade prompts. Verification
  // is only enforced (on the backend) when they quote the public market.
  if (company?.company_type === "CARRIER" && company?.is_external_carrier) {
    return null;
  }
  if (company?.company_type === "CARRIER" && company?.verified_at == null) {
    return <UnverifiedCarrierBanner />;
  }
  // Hold the slot while the payment-method lookup is in flight rather than
  // falling through, which would render the upgrade CTA underneath and then swap.
  if (isPaymentMethodLookupPending) {
    return null;
  }
  // A pay-per-booking carrier without a card cannot quote at all, so this outranks
  // the profile nudge and the plan banners below it.
  if (isPayPerBookingCarrierWithoutPaymentMethod) {
    return <PpbPaymentMethodBanner />;
  }
  // Hold the banner area for a verified carrier until the profile resolves, so
  // we don't flash a subscription banner and then swap to the fill-profile one.
  if (company?.company_type === "CARRIER" && isCarrierProfileLoading) {
    return null;
  }
  if (
    company?.company_type === "CARRIER" &&
    carrierProfile?.profile_completion?.is_complete === false
  ) {
    return <FillCarrierProfileBanner />;
  }
  if (isFreeCarrierSubscription(subscription)) {
    return <FreeCarrierBanner />;
  }
  if (company?.company_type === "CARRIER") {
    return <PaidCarrierBanner />;
  }
  // Any shipper lacking posting permission is not yet verified — prompt them to
  // book an onboarding call. Upgrade CTAs come later, via the plan banners shown
  // once posting permission is granted.
  if (
    company?.company_type === "SHIPPER" &&
    subscription &&
    !subscription.has_shipment_posting_permission
  ) {
    return <UnverifiedShipperBanner />;
  }
  if (subscription?.subscription_plan === "SHIPPER_TRIAL") {
    return <TrialShipperBanner />;
  }
  if (subscription?.subscription_plan === "SHIPPER_FREE_TIER") {
    return <FreeTierShipperBanner />;
  }
  if (company?.company_type === "SHIPPER") {
    return null;
  }

  return (
    <Tooltip title="Finish creating your company profile to access the full app.">
      <Button
        component={RouterLink}
        to="/setup-company"
        variant="contained"
        size="large"
        sx={BANNER_CTA_BUTTON_SX}
      >
        Complete Company Setup
      </Button>
    </Tooltip>
  );
}

export function Banner(props: Props) {
  const { isMobile, openNavBarIcon } = props;
  const { pathname } = useLocation();
  const { company, isLoading } = useRoleContext();
  const { subscription } = useSubscriptionStatus();
  const carrierCompanyId =
    company?.company_type === "CARRIER" ? company.id : null;
  const { companyProfile, loading: isCarrierProfileLoading } =
    useCompanyProfile(carrierCompanyId);
  // External and unverified carriers return earlier in the chain and so can never
  // reach the banner; keeping them out of `enabled` spares them a Stripe round trip
  // for an answer nothing reads.
  const isPayPerBookingCarrier =
    carrierCompanyId != null &&
    !company?.is_external_carrier &&
    company?.verified_at != null &&
    subscription?.subscription_plan === "CARRIER_PAY_PER_BOOKING";
  const {
    hasPaymentMethod,
    loading: isPaymentMethodLoading,
    error: paymentMethodError,
  } = useCompanyHasPaymentMethod({
    companyId: carrierCompanyId,
    enabled: isPayPerBookingCarrier,
  });
  const isPaymentMethodLookupPending =
    isPayPerBookingCarrier && isPaymentMethodLoading;
  // A failed lookup falls through the rest of the chain, which costs the nudge
  // rather than the whole bar.
  const isPayPerBookingCarrierWithoutPaymentMethod =
    isPayPerBookingCarrier &&
    hasPaymentMethod === false &&
    paymentMethodError == null;

  return (
    <AppBar position="static" sx={{ backgroundColor: BG_ICON_COLOR }}>
      {/*
      Anchors the carrier search field, which drops below the bar as a
      full-width strip on phones.
      */}
      {/*
      The gap keeps the centred banner copy off the company name rendered in the
      left section — without it the two sections abut and the text reads as one
      run-on string.
      */}
      <Toolbar sx={{ position: "relative", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {isMobile ? openNavBarIcon : <CompanyProfileBanner />}
        </Box>
        <Box
          sx={{
            flex: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {getBannerContent({
            company,
            subscription,
            isLoading,
            carrierProfile: companyProfile,
            isCarrierProfileLoading,
            isPaymentMethodLookupPending,
            isPayPerBookingCarrierWithoutPaymentMethod,
          })}
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Stack direction="row" color="white" alignItems="center">
            <CarrierSearchBar />
            <NavBarButtonWrapper
              isActive={isRouteActive(pathname, NAV_ROUTES.ACCOUNT, true)}
            >
              <UserAccountButton />
            </NavBarButtonWrapper>
            <LogoutButton />
          </Stack>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
